import { useEffect, useState } from 'react';
import { X, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import type { ProgressPhoto } from '@/types';

interface PhotoViewerProps {
  photo: ProgressPhoto | null;
  photos: ProgressPhoto[];
  onClose: () => void;
  onDelete?: (photoId: string) => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
}

export default function PhotoViewer({ 
  photo, 
  photos, 
  onClose, 
  onDelete,
  onNavigate 
}: PhotoViewerProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!photo) return;

      switch (event.key) {
        case 'Escape':
          if (showDeleteConfirm) {
            setShowDeleteConfirm(false);
          } else {
            onClose();
          }
          break;
        case 'ArrowLeft':
          if (onNavigate && !showDeleteConfirm) {
            onNavigate('prev');
          }
          break;
        case 'ArrowRight':
          if (onNavigate && !showDeleteConfirm) {
            onNavigate('next');
          }
          break;
        case 'Delete':
        case 'Backspace':
          if (!showDeleteConfirm && onDelete) {
            setShowDeleteConfirm(true);
          }
          break;
      }
    };

    if (photo) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [photo, onClose, onNavigate, onDelete, showDeleteConfirm]);

  // Handle photo deletion
  const handleDelete = async () => {
    if (!photo || !onDelete) return;

    setIsDeleting(true);
    try {
      await onDelete(photo.id);
      toast.success('Photo deleted successfully');
      onClose();
    } catch (error) {
      console.error('Failed to delete photo:', error);
      toast.error('Failed to delete photo');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Unknown date';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Don't render if no photo
  if (!photo) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
      {/* Modal Overlay - Click to close */}
      <div 
        className="absolute inset-0 cursor-pointer"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative max-w-4xl max-h-full w-full h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-black bg-opacity-50 text-white">
          <div className="flex items-center space-x-4">
            <Calendar className="w-5 h-5" />
            <span className="text-sm">
              {formatDate(photo.photoDate || photo.uploadDate || photo.createdAt)}
            </span>
            {photo.fileSize && (
              <>
                <span className="text-gray-400">•</span>
                <span className="text-sm text-gray-300">
                  {formatFileSize(photo.fileSize)}
                </span>
              </>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {onDelete && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                title="Delete photo"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              title="Close (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Photo Display */}
        <div className="flex-1 flex items-center justify-center p-4">
          <img
            src={photo.url}
            alt={`Progress photo from ${formatDate(photo.photoDate || photo.uploadDate || photo.createdAt)}`}
            className="max-w-full max-h-full object-contain"
            onError={(_e) => {
              console.error('Failed to load photo:', photo.url);
              toast.error('Failed to load photo');
            }}
          />
        </div>

        {/* Navigation hints */}
        {onNavigate && photos.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded">
            Use arrow keys to navigate • {photos.findIndex(p => p.id === photo.id) + 1} of {photos.length}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Delete Photo</h3>
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this photo? This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}